import React, { useState, useRef, useEffect, useMemo } from "react";
import {
    HiOutlineBuildingOffice2,
    HiOutlineXMark,
    HiOutlineMagnifyingGlass,
    HiOutlineHeart,
    HiHeart,
    HiArrowUturnLeft
} from "react-icons/hi2";
import "../styles/CompanySearch.css";
import * as Sentry from '@sentry/react';

function CompanySearch({
    user = {},
    setUser = () => {},
    selectedCompanyIds = [],
    setSelectedCompanyIds = () => {},
    favoriteCompanyIds = [],
    onToggleFavoriteCompany = () => {},
    filtersDisabled = false,
    setErrorMessage = () => {},
    setShowErrorPopup = () => {},
    onLoadCompanyJobs = () => {},
    allCompanies = [],
    companiesLoading = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchRef = useRef(null);
    const dropdownRef = useRef(null);

    const MAX_SELECTED_COMPANIES = 5;
    const MAX_FREE_FAVORITE_COMPANIES = 3;

    const isCompanyLimitReached =
        selectedCompanyIds.length >= MAX_SELECTED_COMPANIES;

    const isPremiumUser = user?.plan === "basic";
    const hasReachedFreeFavoriteLimit =
        !isPremiumUser &&
        (favoriteCompanyIds?.length || 0) >= MAX_FREE_FAVORITE_COMPANIES;

    // Build a fast lookup map: company id -> full company object
    const companiesById = useMemo(() => {
        const map = new Map();
        allCompanies.forEach((company) => {
            if (company?.id != null) {
                map.set(company.id, company);
            }
        });
        return map;
    }, [allCompanies]);

    // Helper to get full company object from an id
    const getCompanyById = (companyId) => companiesById.get(companyId) || null;

    // Full selected company objects derived from selectedCompanyIds
    const selectedCompanies = useMemo(() => {
        return selectedCompanyIds
            .map((companyId) => getCompanyById(companyId))
            .filter(Boolean);
    }, [selectedCompanyIds, companiesById]);

    // Filter companies based on search query
    const filteredCompanies = useMemo(() => {
        if (!searchQuery.trim()) return allCompanies;

        const query = searchQuery.toLowerCase().trim();
        return allCompanies.filter((company) =>
            company.name?.toLowerCase().includes(query)
        );
    }, [allCompanies, searchQuery]);

    const MAX_RENDERED = 100;

    const shownCompanies = useMemo(() => {
        return filteredCompanies.slice(0, MAX_RENDERED);
    }, [filteredCompanies]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const isCompanySelected = (companyId) => {
        return selectedCompanyIds.includes(companyId);
    };

    const handleSelectCompany = (companyId) => {
        const isAlreadySelected = selectedCompanyIds.includes(companyId);

        let newSelection;

        if (isAlreadySelected) {
            newSelection = selectedCompanyIds.filter((id) => id !== companyId);
        } else {
            if (isCompanyLimitReached) return;
            newSelection = [...selectedCompanyIds, companyId];
        }

        setSelectedCompanyIds(newSelection);
        onLoadCompanyJobs(newSelection);
        setSearchQuery("");
    };

    const handleRemoveCompany = (e, companyId) => {
        e.stopPropagation();
        const newSelection = selectedCompanyIds.filter((id) => id !== companyId);
        setSelectedCompanyIds(newSelection);
        onLoadCompanyJobs(newSelection);
    };

    const restoreRemovedCompany = async (companyId) => {
        if (!companyId) return;

        setUser((prev) => {
            const existingRemovedCompanies = prev.removed_companies || [];

            return {
                ...prev,
                removed_companies: existingRemovedCompanies.filter(
                    (id) => id !== companyId
                ),
            };
        });

        try {
            await fetch("/api/remove_company", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    company_id: companyId,
                }),
            });
        } catch (err) {
            Sentry.captureException(err);
            alert("Failed to restore company.");
        }
    };

    const handleClearSelection = (e) => {
        e.stopPropagation();
        setSelectedCompanyIds([]);
        setSearchQuery("");
        setIsOpen(false);
        onLoadCompanyJobs([]);
    };

    const handleFavoriteClick = (e, companyId, alreadyFavorited) => {
        e.stopPropagation();

        const shouldBlockFavorite =
            !alreadyFavorited &&
            !isPremiumUser &&
            (favoriteCompanyIds?.length || 0) >= MAX_FREE_FAVORITE_COMPANIES;

        if (shouldBlockFavorite) {
            return;
        }

        onToggleFavoriteCompany(companyId, alreadyFavorited);
    };

    const isCompanyFavorited = (companyId) => {
        return favoriteCompanyIds.includes(companyId);
    };

    const isCompanyRemoved = (companyId) => {
        return user?.removed_companies?.includes(companyId) ?? false;
    };

    const CompanyLogo = ({ company }) => {
        const [logoError, setLogoError] = useState(false);

        if (company?.logo && !logoError) {
            return (
                <img
                    src={company.logo}
                    alt={`${company.name} logo`}
                    className="company-search-logo"
                    onError={() => setLogoError(true)}
                />
            );
        }

        return (
            <div className="company-search-logo-fallback">
                <HiOutlineBuildingOffice2 size={18} />
            </div>
        );
    };

    const hasCompanies = !companiesLoading && allCompanies.length > 0;

    const selectedPositionsCount = selectedCompanies.reduce(
        (sum, company) => sum + (company.activePositionCount || 0),
        0
    );

    return (
        <div className="company-search-wrapper" ref={dropdownRef}>
            <div className="company-search-label">
                <HiOutlineBuildingOffice2 size={16} />
                <span>Search by Company</span>

                {companiesLoading ? (
                    <span className="company-search-total-badge">
                        Loading companies...
                    </span>
                ) : hasCompanies ? (
                    <span className="company-search-total-badge">
                        {allCompanies.length < 5000
                            ? `${allCompanies.length} companies`
                            : `${Math.floor(allCompanies.length / 5000) * 5000}+ companies`}
                    </span>
                ) : null}
            </div>

            <div
                className={`company-search-input-container ${isOpen ? "is-focused" : ""
                    } ${selectedCompanies.length > 0 ? "has-selection" : ""}`}
                onClick={() => {
                    if (filtersDisabled || isCompanyLimitReached) return;
                    setIsOpen(true);
                }}
            >
                <HiOutlineMagnifyingGlass className="company-search-icon" size={18} />

                <input
                    ref={searchRef}
                    id="company-search-input"
                    type="text"
                    className="company-search-input"
                    placeholder={
                        isCompanyLimitReached
                            ? `Company limit reached (${MAX_SELECTED_COMPANIES} max)`
                            : selectedCompanies.length > 0
                                ? "Add another company..."
                                : "Search for a company..."
                    }
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    disabled={filtersDisabled || isCompanyLimitReached}
                    aria-label="Search for companies to filter by"
                />

                {selectedCompanies.length > 0 && (
                    <button
                        className="company-search-clear-btn"
                        onClick={handleClearSelection}
                        title="Clear all company filters"
                        disabled={filtersDisabled}
                        aria-label="Clear all company filters"
                    >
                        <HiOutlineXMark size={18} />
                    </button>
                )}
            </div>

            {selectedCompanies.length > 0 && (
                <div className="company-search-selected-chips">
                    {selectedCompanies.map((company) => (
                        <div key={company.id} className="company-search-chip">
                            <CompanyLogo company={company} />
                            <span className="company-search-chip-name">{company.name}</span>
                            <span className="company-search-matches-count">
                                {company.matchesCount || 0}
                            </span>
                            <span className="company-search-chip-count">
                                {company.activePositionCount || 0}
                            </span>
                            <button
                                className="company-search-chip-remove"
                                onClick={(e) => handleRemoveCompany(e, company.id)}
                                title="Remove company"
                            >
                                <HiOutlineXMark size={14} />
                            </button>
                        </div>
                    ))}

                    <div className="company-search-selected-summary">
                        {selectedPositionsCount} total position
                        {selectedPositionsCount !== 1 ? "s" : ""} from{" "}
                        {selectedCompanies.length} compan
                        {selectedCompanies.length !== 1 ? "ies" : "y"}
                    </div>
                </div>
            )}

            {isOpen && (
                <div className="company-search-dropdown">
                    <div className="company-search-dropdown-header">
                        <span className="company-search-dropdown-title">
                            {searchQuery ? `Results for "${searchQuery}"` : "All Companies"}
                        </span>
                        <span className="company-search-dropdown-count">
                            {filteredCompanies.length} compan
                            {filteredCompanies.length !== 1 ? "ies" : "y"}
                        </span>
                    </div>

                    <div className="company-search-dropdown-list">
                        {companiesLoading ? (
                            <div className="company-search-no-results">
                                <HiOutlineBuildingOffice2 size={24} />
                                <span>Companies Loading...</span>
                            </div>
                        ) : filteredCompanies.length === 0 ? (
                            <div className="company-search-no-results">
                                <HiOutlineBuildingOffice2 size={24} />
                                <span>No companies match your search</span>
                            </div>
                        ) : (
                            shownCompanies.map((company) => {
                                const isRemoved = isCompanyRemoved(company.id);
                                const isFavorited = isCompanyFavorited(company.id);
                                const favoriteLimitReachedForThisCompany =
                                    !isPremiumUser &&
                                    !isFavorited &&
                                    hasReachedFreeFavoriteLimit;

                                return (
                                    <div
                                        key={company.id}
                                        className={`company-search-item ${isCompanySelected(company.id) ? "is-selected" : ""
                                            }`}
                                        onClick={() => handleSelectCompany(company.id)}
                                    >
                                        <div className="company-search-item-checkbox">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${company.name}`}
                                                checked={isCompanySelected(company.id)}
                                                onChange={() => {}}
                                                onClick={() => handleSelectCompany(company.id)}
                                            />
                                        </div>

                                        <CompanyLogo company={company} />

                                        <div className="company-search-item-info">
                                            <span className="company-search-item-name">
                                                {company.name}
                                            </span>
                                            {company.industry && (
                                                <span className="company-search-item-industry">
                                                    {company.industry}
                                                </span>
                                            )}
                                        </div>

                                        <div className="company-search-item-meta">
                                            <div className="company-search-counts-meta">
                                                {isRemoved ? (
                                                    <span className="company-search-removed-chip">
                                                        Removed
                                                    </span>
                                                ) : (
                                                    <>
                                                        <span className="company-search-matches-count">
                                                            {company.matchesCount || 0} match
                                                            {(company.matchesCount || 0) !== 1 ? "es" : ""}
                                                        </span>

                                                        <span className="company-search-item-count">
                                                            {company.activePositionCount || 0} posting
                                                            {(company.activePositionCount || 0) !== 1 ? "s" : ""}
                                                        </span>
                                                    </>
                                                )}
                                            </div>

                                            {isRemoved ? (
                                                <button
                                                    className="company-search-restore-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        restoreRemovedCompany(company.id);
                                                    }}
                                                    title="Restore company"
                                                >
                                                    <HiArrowUturnLeft size={18} />
                                                </button>
                                            ) : (
                                                <button
                                                    className={`company-search-favorite-btn ${isFavorited ? "is-favorited" : ""
                                                        } ${favoriteLimitReachedForThisCompany ? "is-disabled" : ""}`}
                                                    onClick={(e) =>
                                                        handleFavoriteClick(
                                                            e,
                                                            company.id,
                                                            isFavorited
                                                        )
                                                    }
                                                    title={
                                                        favoriteLimitReachedForThisCompany
                                                            ? "Free plan maximum is 3 favorite companies. Upgrade your plan to save more."
                                                            : isFavorited
                                                                ? "Remove from favorites"
                                                                : "Add to favorites"
                                                    }
                                                    disabled={favoriteLimitReachedForThisCompany}
                                                >
                                                    {isFavorited ? (
                                                        <HiHeart size={18} />
                                                    ) : (
                                                        <HiOutlineHeart size={18} />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export { CompanySearch };